/**
 * S18 — Measure · Cost Optimisation (`/measure/cost`).
 *
 * Wave 31 — Phase 3b. Migrated off `useTenantData()` onto the new
 * `/api/metrics/cost` REST endpoint via the `useEntity()` hook. The
 * endpoint returns `{ costTrend, costOpportunities, costByDepartment }`
 * in one call; we hold the whole envelope in a single hook.
 */
import React, { useMemo } from "react";
import { DollarSign, TrendingDown, PiggyBank, Target, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntity } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { cn } from "@/lib/utils";

const A = (n) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(n);

// Compact AUD axis label — "A$1.5M", "A$380k", "A$0".
function fmtAxisAud(v) {
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    return `A$${m < 10 ? m.toFixed(1) : Math.round(m)}M`;
  }
  if (v >= 1_000) return `A$${Math.round(v / 1_000)}k`;
  return `A$${Math.round(v)}`;
}

// Round positive numbers up to the next nice ceiling (1, 2, 2.5, or 5
// times a power of ten) so Y-axis tick labels read cleanly.
function niceCeil(v) {
  if (v <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / pow;
  // Snap to the next step in the [1, 2, 2.5, 5, 10] axis-tick ladder.
  const niceNorm = pickNiceTick(norm);
  return niceNorm * pow;
}

function pickNiceTick(norm) {
  if (norm <= 1) return 1;
  if (norm <= 2) return 2;
  if (norm <= 2.5) return 2.5;
  if (norm <= 5) return 5;
  return 10;
}

export default function CostOptimisation() {
  const { user } = useAuth();
  const { isCustomer } = useTenantData();
  const {
    data,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useEntity("/api/metrics/cost");

  const costTrend = useMemo(() => data?.costTrend || [], [data]);
  const costOpportunities = useMemo(() => data?.costOpportunities || [], [data]);
  const costByDepartment = useMemo(() => data?.costByDepartment || [], [data]);

  const totalSpend = useMemo(() => {
    if (!costTrend.length) return 0;
    const last = costTrend[costTrend.length - 1];
    return last.inference + last.embedding + last.rag + last.egress;
  }, [costTrend]);
  const totalSavings = costOpportunities.reduce((acc, o) => acc + o.estSavingsAud, 0);
  const approvedSavings = costOpportunities
    .filter((o) => o.status === "approved")
    .reduce((acc, o) => acc + o.estSavingsAud, 0);

  if (loading && !data) return <Skeleton />;
  if (error) return <ErrorView message={error.message} onRetry={refetch} />;
  if (isCustomer && !costTrend.length) {
    return <Empty userName={user?.name?.split(" ")[0]} />;
  }

  const maxSpend = Math.max(
    ...costTrend.map((w) => w.inference + w.embedding + w.rag + w.egress),
    1,
  );
  const deptTotal = costByDepartment.reduce((a, d) => a + d.spendAud, 0) || 1;

  return (
    <div
      className="relative px-6 lg:px-8 py-6 anim-fade-up"
      data-testid="cost-optimisation"
    >
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Measure · Cost Optimisation</p>
          <h1 className="text-display-md text-ink-primary mt-1">Cost Optimisation</h1>
          <p className="text-ink-secondary mt-1 max-w-2xl">
            Weekly AI spend trend with savings opportunities — each carrying an
            estimated dollar value and the quality-impact delta you&rsquo;d
            accept to bank it.
          </p>
        </div>
        <SyncedIndicator
          lastSyncedAt={lastSyncedAt}
          loading={loading}
          onRefetch={refetch}
          testId="cost-optimisation-synced"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiTile label="Current week spend" value={A(totalSpend)} delta="this week" deltaTone="neutral" hint="inference + embedding + RAG + egress" icon={DollarSign} tone="tralenex" testId="cost-kpi-spend" />
        <KpiTile label="Identified savings" value={A(totalSavings)} delta={`${costOpportunities.length} opportunities`} deltaTone="up" hint="annualised" icon={PiggyBank} tone="gold" testId="cost-kpi-savings" />
        <KpiTile label="Approved savings" value={A(approvedSavings)} delta="already in flight" deltaTone="up" hint="route to change request" icon={TrendingDown} tone="low" testId="cost-kpi-approved" />
        <KpiTile label="Open opportunities" value={costOpportunities.filter((o) => o.status === "open").length.toString()} delta="ready to triage" deltaTone="neutral" hint="awaiting BU review" icon={Target} tone="risk" testId="cost-kpi-open" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="surface-card shadow-e1 p-4 lg:col-span-2" data-testid="cost-trend">
          <p className="eyebrow">12-week spend trend (AUD)</p>
          {(() => {
            // Round the chart ceiling up to a "nice" number so the Y-axis
            // reads cleanly (e.g. A$0 / A$50k / A$100k / A$150k / A$200k).
            const niceMax = niceCeil(maxSpend);
            const stops = [1, 0.75, 0.5, 0.25, 0]; // top → bottom
            return (
              <div className="mt-3 flex gap-2">
                {/* Y-axis labels column */}
                <div
                  className="flex flex-col justify-between h-56 text-[10px] font-mono text-ink-secondary tabular-nums shrink-0 pr-1 text-right"
                  aria-hidden="true"
                  data-testid="cost-trend-yaxis"
                >
                  {stops.map((p) => (
                    <span key={p}>{fmtAxisAud(niceMax * p)}</span>
                  ))}
                </div>
                {/* Bars + X-axis */}
                <div className="flex-1 min-w-0">
                  <div className="h-56 flex items-end gap-1.5 border-l border-border-subtle">
                    {costTrend.map((w) => {
                      const total = w.inference + w.embedding + w.rag + w.egress;
                      const inf = (w.inference / total) * 100;
                      const emb = (w.embedding / total) * 100;
                      const rag = (w.rag / total) * 100;
                      const totalH = (total / niceMax) * 100;
                      return (
                        <div
                          key={w.week}
                          className="flex-1 flex flex-col items-center justify-end h-full"
                          data-testid={`cost-bar-${w.week}`}
                        >
                          <div
                            className="w-full rounded-t overflow-hidden"
                            style={{ height: `${totalH}%` }}
                            title={`${w.week} · ${A(total)}`}
                          >
                            <div className="bg-tralenex-600" style={{ height: `${inf}%` }} />
                            <div className="bg-tralenex-400" style={{ height: `${emb}%` }} />
                            <div className="bg-gold-400" style={{ height: `${rag}%` }} />
                            <div className="bg-ink-disabled" style={{ height: `${100 - inf - emb - rag}%` }} />
                          </div>
                          <p className="text-meta text-ink-secondary mt-1 font-mono">{w.week}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
          <div className="mt-4 flex flex-wrap gap-3 text-meta text-ink-secondary">
            <Legend color="bg-tralenex-600" label="Inference" />
            <Legend color="bg-tralenex-400" label="Embedding" />
            <Legend color="bg-gold-400" label="RAG" />
            <Legend color="bg-ink-disabled" label="Egress" />
          </div>
        </div>

        <div className="surface-card shadow-e1 p-4" data-testid="cost-by-dept">
          <p className="eyebrow">Spend by department</p>
          <ul className="mt-3 space-y-2.5">
            {costByDepartment.map((d) => {
              const pct = (d.spendAud / deptTotal) * 100;
              return (
                <li
                  key={d.name}
                  className="text-sm"
                  data-testid={`cost-dept-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex justify-between text-ink-primary">
                    <span>{d.name}</span>
                    <span className="font-mono tabular-nums">{A(d.spendAud)}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded bg-inset/60 overflow-hidden">
                    <div className="h-full bg-tralenex-500" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-meta text-ink-secondary mt-0.5">
                    {d.agents} agents · {pct.toFixed(1)}%
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="mt-6 surface-card shadow-e1 overflow-hidden" data-testid="cost-opportunities">
        <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between">
          <div>
            <p className="eyebrow">Cost-savings opportunities</p>
            <p className="text-sm text-ink-secondary mt-0.5">
              Routable into a deployment change request — each carries a
              quality-impact estimate.
            </p>
          </div>
          <p className="text-meta text-ink-secondary tabular-nums">
            {costOpportunities.length} items
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cost-opportunities-table">
            <thead className="bg-inset/40 text-ink-secondary text-meta uppercase tracking-[0.06em]">
              <tr>
                {["ID", "Opportunity", "Agent", "Savings (yr)", "Quality impact", "Confidence", "Status", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {costOpportunities.map((o) => (
                <tr
                  key={o.id}
                  className="border-t border-border-subtle hover:bg-inset/30"
                  data-testid={`cost-row-${o.id}`}
                >
                  <td className="px-3 py-2 font-mono text-meta text-ink-secondary whitespace-nowrap">{o.id}</td>
                  <td className="px-3 py-2 text-ink-primary min-w-[280px]">{o.title}</td>
                  <td className="px-3 py-2 text-ink-primary whitespace-nowrap">{o.agentName}</td>
                  <td className="px-3 py-2 font-mono tabular-nums text-tralenex-700 font-semibold whitespace-nowrap">{A(o.estSavingsAud)}</td>
                  <td className="px-3 py-2 text-ink-primary whitespace-nowrap text-meta">{o.qualityImpact}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-meta uppercase tracking-[0.06em] font-bold">{o.confidence}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span
                      className={cn(
                        "inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.06em]",
                        o.status === "approved"
                          ? "bg-risk-low/10 text-risk-low"
                          : o.status === "in-review"
                            ? "bg-gold-300/15 text-gold-700"
                            : "bg-tralenex-600/10 text-tralenex-700",
                      )}
                    >
                      {o.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-meta text-ink-disabled">
                    <ArrowRight size={12} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn("w-2.5 h-2.5 rounded-sm", color)} />
      {label}
    </span>
  );
}

function Skeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="cost-optimisation-loading">
      <div className="h-7 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[280px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function ErrorView({ message, onRetry }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="cost-optimisation-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Measure · Cost Optimisation</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load cost data</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="cost-optimisation-retry"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function Empty({ userName }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden" data-testid="cost-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-2xl">
        <p className="eyebrow">Measure · Cost Optimisation</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 text-[15px]">
          No spend data yet. Connect a vendor and start running agents — once
          cost telemetry flows, ALGOS surfaces routable savings here.
        </p>
      </div>
    </div>
  );
}
